import { Button, ZeroState } from "@codat/orchard-ui";
import { FileOutputType } from "api/public-service";
import { useLocalStorage } from "contexts/LocalStorageContext";
import { usePublicApiService } from "contexts/PublicApiServiceProvider";
import { useSpendInsightsProductSettings } from "contexts/SpendInsightsProductSettingsContext";
import { useSpendInsightsCompanies } from "hooks/query/useSpendInsightsCompanies";
import { usePageNumberRouting } from "hooks/usePageNumberRouting";
import { useFeatureStatus } from "portal-ui-remote/FeatureStatusContext";
import { useUserState } from "portal-ui-remote/UserStateContext";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDownloadReportFile } from "utils/downloadReportFile";
import formatUrl from "utils/formatUrl";

import { paths } from "components/App/paths";
import CompanySearch from "components/CompanySearch";
import Page from "components/Page";
import PageHeader from "components/PageHeader";
import NotFound from "pages/NotFound";

import { CompaniesTable } from "./components/CompaniesTable";
import styles from "./SpendInsights.module.scss";

const SpendInsights = () => {
  const history = useHistory();
  const { userState } = useUserState();
  const localStorageService = useLocalStorage();
  const publicApi = usePublicApiService();
  const spendInsightsSettings = useSpendInsightsProductSettings();
  const {
    featureStatus,
    loading: featureStatusLoading,
    error: featureStatusError,
  } = useFeatureStatus();
  const downloadReportFile = useDownloadReportFile();

  const searchParams = new URLSearchParams(location.search);
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") ?? ""
  );

  const { page, setPage } = usePageNumberRouting();
  const [pageSize, setPageSize] = useState(
    () => localStorageService.pageSize() ?? 10
  );

  useEffect(() => {
    const downloadSearchParams = new URLSearchParams(location.search);
    const companyId = downloadSearchParams.get("search");
    const reportType = downloadSearchParams.get("downloadReportType");
    const outputName = downloadSearchParams.get(
      "downloadOutputType"
    ) as FileOutputType;

    if (companyId && reportType && outputName) {
      downloadReportFile(
        publicApi.getSpendReport({
          params: {
            companyId,
            reportType,
            reportId: "latest",
            outputName,
          },
        })
      );

      downloadSearchParams.delete("downloadReportType");
      downloadSearchParams.delete("downloadOutputType");
      history.push({ search: downloadSearchParams.toString() });
    }
  }, [downloadReportFile, history, publicApi]);

  useEffect(() => {
    localStorageService.setPageSize(pageSize);
  }, [pageSize, localStorageService]);

  const spendInsightsEnabled =
    !spendInsightsSettings.isError &&
    !spendInsightsSettings.isLoading &&
    !featureStatusError &&
    !featureStatusLoading &&
    featureStatus?.["PortalUI.ShowSpendInsightsTable"];

  const { data, isLoading } = useSpendInsightsCompanies({
    enabled: !!spendInsightsEnabled,
    queryParams: {
      page,
      pageSize,
      query: searchQuery,
    },
  });

  if (spendInsightsSettings.isLoading) {
    return null;
  }

  if (!spendInsightsEnabled) {
    return <NotFound />;
  }

  const noResults = !isLoading && !data?.results.length;

  return (
    <Page>
      <PageHeader header="Spend Insights">
        <div className={styles.tableHeader}>
          <CompanySearch
            className={styles.searchInput}
            onDebouncedValueChange={(value: string) => {
              searchParams.set("search", value);
              history.push({ search: searchParams.toString() });
            }}
            onChange={(value: string) => {
              setPage(1, { replace: page === 1 });
              setSearchQuery(value);
            }}
            value={searchQuery}
            popoverText="Find a company by its name or ID"
          />
          <Button
            data-testid="new-company"
            label="Create company"
            onClick={() =>
              history.push(
                formatUrl(
                  paths.COMPANIES,
                  {},
                  {
                    newCompany: true,
                    newCompanyProducts: "supplier-enablement",
                  }
                )
              )
            }
            isSkeleton={!userState}
          />
        </div>
      </PageHeader>

      {noResults ? (
        <ZeroState
          className={styles.zeroState}
          title="No companies found"
          description={
            searchQuery ? "Please try a different search term" : undefined
          }
          ctaText={searchQuery ? "Clear search" : undefined}
          onCtaClick={() => setSearchQuery("")}
        />
      ) : (
        <CompaniesTable
          isSkeleton={isLoading}
          data={data}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      )}
    </Page>
  );
};

export default SpendInsights;
